<template>
    <tailwind-layout title="Landlord Insurance">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"><!-- Block for lg and larger -->
                <div class="flex flex-col">
                    <div class="flex items-center gap-5">
                        <img :src="appUrl('/steadily-logo.png')" class="w-20 h-20">
                        <div>
                            <h1 class="font-semibold text-2xl leading-tight">Landlord Insurance</h1>
                            <h2 class="text-xs">Add a property and get a custom quote from Steadily to insure your rental.</h2>
                        </div>
                    </div>
                    
                </div>
                <div class="flex items-center">
                    <inertia-link class="primaryButton" :href="route('insurance.create-property')" data-demo-target="add-property-btn">
                        Add Property
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex items-center w-full">
                    <img :src="appUrl('/steadily-logo.png')" class="w-20 h-20">
                    <div class="flex flex-col">
                        <h1 class="font-semibold text-2xl leading-tight">Landlord Insurance</h1>
                    </div>
                    <inertia-link class="primaryButton ml-auto" :href="route('insurance.create-property')" data-demo-target="add-property-btn">
                        Add Property
                    </inertia-link>
                </div>
                <h2 class="text-xs">Add a property and get a custom quote from Steadily to insure your rental.</h2>
            </div>
        </template>
        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch ml-2 lg:ml-0">
                    <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of Properties per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <input id="search" v-model="form.search" type="text" placeholder="Search for Properties..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md sm:mx-2">
                        <div class="flex items-center mt-2 md:ml-2" @click="show_archived_properties()">
                            <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                    <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                </Switch>
                                <SwitchLabel as="span" class="ml-3 text-sm">
                                    <span class="font-medium text-gray-700">Show Archived Properties</span>
                                </SwitchLabel>
                            </SwitchGroup>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table" data-demo-target="properties-table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Address
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Size
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Year Built
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Monthly Premium
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">

                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200">
                                    <tr v-for="(property, index) in properties.data" :key="index"
                                           :class="[(index % 2 === 0 && !property.insurance_property_deleted_at ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : (!property.insurance_property_deleted_at) ? 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer' : ''),
                                        (property.insurance_property_deleted_at ? 'bg-red-100 !border-b !border-red-200 hover:bg-red-200 hover:cursor-pointer' : '')]"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ property.full_address }}
                                            <span v-if="property.insurance_property_deleted_at" class="text-red-700 bg-red-50 ring-red-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset">
                                                Archived
                                            </span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ property.steadily_quote?.size_sqft }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ property.steadily_quote?.year_built }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span v-if="property.steadily_quote?.dp_3">${{ property.steadily_quote?.formatted_dp3 }}</span>
                                        </td>
                                        <td colspan="4" class="text-right">
                                            <span class="isolate inline-flex rounded-md shadow-sm mr-3">
                                                <button
                                                    v-if="!property.insurance_property_deleted_at"
                                                    title="Get Quote"
                                                    @click="getInsurance(property)"
                                                    class="relative -ml-px inline-flex items-center rounded-l-md bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10"
                                                    data-demo-target="get-quote-btn">
                                                    Get Insurance Quote
                                                </button>
                                                <inertia-link
                                                    v-if="!property.insurance_property_deleted_at"
                                                    title="Edit Property"
                                                    :href="route('insurance.edit-property', property.id)"
                                                    class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Edit Property
                                                </inertia-link>

                                                <button
                                                    v-if="!property.insurance_property_deleted_at"
                                                    title="Archive Property"
                                                    @click="archiveProperty(property)"
                                                    class="relative -ml-px inline-flex items-center rounded-r-md bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Archive Property
                                                </button>
                                                <button
                                                    v-else
                                                    title="Archive Property"
                                                    @click="restoreProperty(property)"
                                                    class="relative -ml-px inline-flex items-center rounded-r-md bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Restore Property
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-show="!properties.data.length">
                                        <td colspan="5" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No Properties were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(property, index) in properties.data" :key="index" :class="[property.deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
                                        <inertia-link :href="route('properties.show', property.id)">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">{{ property.full_address }}</h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Size:</span> {{ property.steadily_quote?.size_sqft }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Year Built:</span> {{ property.steadily_quote?.year_built }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Monthly Premium:</span> <span v-if="property.steadily_quote?.dp_3">${{ property.steadily_quote?.formatted_dp3 }}</span></h4>
                                        </div>
                                        </inertia-link>
                                        <div>
                                            <div :class="[property.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                <div class="flex w-0 flex-1">
                                                    <button v-if="!property.insurance_property_deleted_at" type="button" @click="getInsurance(property)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-eyes" />
                                                        Get Insurance Quote
                                                    </button>
                                                </div>
                                                <div v-if="!property.insurance_property_deleted_at" class="-ml-px flex w-0 flex-1">
                                                    <inertia-link :href="route('insurance.edit-property', property.id)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-edit" />
                                                        Edit
                                                    </inertia-link>
                                                </div>
                                            </div>
                                            <div :class="[property.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                <div class="flex w-0 flex-1">
                                                    <button v-if="!property.insurance_property_deleted_at" type="button" @click="archiveProperty(property)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-trash" />
                                                        Archive Property
                                                    </button>
                                                    <button v-else type="button" @click="restoreProperty(property)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-refresh" />
                                                        Restore Property
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="properties.links" :results="properties" :type="'properties'" />
        </div>

        <modal-jetstream :show="getInsuranceModal" :max-width="'3xl'">
            <div class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h1 class="text-2xl font-bold text-gray-800">{{ insuranceProperty.full_address }}</h1>
                    <button @click="getInsuranceModal = false"
                        class="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <!-- eslint-disable-next-line vue/no-undef-components -->
                        <x-heroicon-o-x class="w-6 h-6" />
                    </button>
                </div>
                <form v-if="!quoteReceived" @submit.prevent="submitInsuranceForm">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div>
                            <label class="text-sm font-medium text-gray-700">Size (Sqft) <span class="text-xs text-red-600">*</span></label>
                            <input type="number" v-model="insuranceForm.size_sqft"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                            <!-- eslint-disable-next-line vue/no-undef-properties -->
                            <span v-if="errors.size_sqft" class="text-red-500 text-sm">{{ errors.size_sqft[0] }}</span>
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700">Year Built <span class="text-xs text-red-600">*</span></label>
                            <select v-model="insuranceForm.year_built" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm">
                                <option disabled value="">Select year built...</option>
                                <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                            </select>
                            <!-- eslint-disable-next-line vue/no-undef-properties -->
                            <span v-if="errors.year_built" class="text-red-500 text-sm">{{ errors.year_built[0] }}</span>
                        </div>

                        <div>
                            <label class="text-sm font-medium text-gray-700">Property Type <span class="text-xs text-red-600">*</span></label>
                            <select v-model="insuranceForm.property_type"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm">
                                <option disabled="" value="">Select property type...</option>
                                <option value="SINGLE_FAMILY">Single Family</option>
                                <option value="DUPLEX">Duplex</option>
                                <option value="TRIPLEX">Triplex</option>
                                <option value="QUADPLEX">Quadplex</option>
                                <option value="APARTMENT_BUILDING">Apartment Building</option>
                                <option value="MOBILE_HOME">Mobile Home</option>
                                <option value="OTHER">Other</option>
                            </select>
                            <!-- eslint-disable-next-line vue/no-undef-properties -->
                            <span v-if="errors.property_type" class="text-red-500 text-sm">{{ errors.property_type[0] }}</span>
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700">Number of Units</label>
                            <input v-model="insuranceForm.number_of_units" type="number"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                            <!-- eslint-disable-next-line vue/no-undef-properties -->
                            <span v-if="errors.number_of_units" class="text-red-500 text-sm">{{ errors.number_of_units[0] }}</span>
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700">Stories</label>
                            <input v-model="insuranceForm.stories" type="number"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                            <!-- eslint-disable-next-line vue/no-undef-properties -->
                            <span v-if="errors.stories" class="text-red-500 text-sm">{{ errors.stories[0] }}</span>
                        </div>
                    </div>

                    <p v-if="apiError" class="text-xs text-red-600 bg-red-100 p-2 mt-2">{{ apiError }}</p>

                    <div class="flex justify-end space-x-3 mt-6">
                        <button @click="getInsuranceModal = false"
                            class="closeButton">
                            Close
                        </button>
                        <button type="submit" :disabled="isGetQuoteButtonDisabled"
                            class="primaryButton" :class="{ 'opacity-25': isGetQuoteButtonDisabled }">
                            Get Insurance Quote
                        </button>
                    </div>
                </form>

                <div v-else class="bg-white rounded-lg shadow-md p-6 max-w-lg mx-auto">
                    <div class="flex justify-between items-center border-b border-gray-400 pb-3">
                        <span class="text-purple-700 font-bold text-lg pr-3">STEADILY.</span>
                        <span class="text-purple-600 font-medium border-l pl-3 border-gray-400">
                            {{ insuranceProperty.full_address }}
                            <p class="text-sm">SIZE <strong class="text-black">
                                {{ quote.size_sqft }} SQFT
                            </strong></p>
                            <p class="text-sm">BUILT <strong class="text-black">
                                {{ quote.year_built }}
                            </strong></p>
                           
                        </span>
                    </div>
                    <div class="font-medium border-t border-gray-400 pt-4 flex flex-col items-center">
                        <div class="text-center">
                            <p class="text-gray-500 uppercase text-sm">Monthly Premium</p>
                            <h2 class="text-3xl font-bold text-purple-700">
                                ${{ quote.monthly_premium }}
                            </h2>
                        </div>
                    </div>
                </div>
                <div v-if="quoteReceived" class="flex justify-between items-center mt-6">
                    <!-- Close Button on the Left -->
                    <button @click="getInsuranceModal = false" class="closeButton mr-3">
                        Close
                    </button>
                    
                    <!-- Other Buttons on the Right -->
                    <div class="flex space-x-3">
                        <a target="_blank" :href="quote.start_url" class="primaryButton">
                            Get Insurance Quote
                        </a>
                        <button type="button" @click="quoteReceived = false" class="primaryButton">
                            Update Home Details
                        </button>
                    </div>
                </div>
            </div>
        </modal-jetstream>

        <div v-if="showArchiveModal">
            <TransitionRoot as="template" :show="showArchiveModal">
                <Dialog as="div" class="relative z-50" @close="showArchiveModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">Are you sure you want to archive this property?</DialogTitle>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showArchiveModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="destroy()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
        <div v-if="showRestoreModal">
            <TransitionRoot as="template" :show="showRestoreModal">
                <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this property?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="restore()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
    
    <!-- Demo Widget -->
    <DemoWidget
        :page-identifier="'insurance.properties'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import {Switch, SwitchLabel, SwitchGroup} from "@headlessui/vue";
import ModalJetstream from "@/Jetstream/Modal.vue";
import axios from 'axios'
import DemoWidget from '@/Components/DemoWidget.vue'
import { getDemoSteps } from '@/Config/demoSteps.js'
import { router } from '@inertiajs/vue3'
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowsRotate, faBoxArchive } from '@fortawesome/pro-solid-svg-icons'

export default {
    components: {
        SwitchGroup,
        SwitchLabel,
        Switch,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        ModalJetstream,
        Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot,
        FontAwesomeIcon,
        DemoWidget
    },
    props: {
        errors: null,
        filters: Object,
        properties: Object,
        people: Object,
    },
    data() {
        return {
            form: {
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:25,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            showArchiveModal : false,
            showRestoreModal : false,
            selectedProperty: null,
            getInsuranceModal: false,
            insuranceProperty: null,
            insuranceForm: this.$inertia.form({
                property_id: null,
                state: null,
                zip_code: null,
                street_address: null,
                city: null,
                property_type: null,
                size_sqft: null,
                year_built: null,
                number_of_units: null,
                stories: null,
            }),
            errors: {},
            isGetQuoteButtonDisabled: false,
            apiError: null,
            quoteReceived: false,
            demoSteps: getDemoSteps('insurance.properties'),
            quote: {
                address: null,
                size_sqft: null,
                year_built: null,
                monthly_premium: null,
                start_url: null,
            },
            faArrowsRotate,
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('insurance.properties'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    computed: {
        years() {
            const startYear = 1800;
            const currentYear = new Date().getFullYear();
            return Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);
        },
    },
    methods: {
        show_archived_properties() {
            this.form.trashed = !this.form.trashed;
        },
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },

        archiveProperty(property) {
            this.selectedProperty = property;
            this.showArchiveModal = true;
        },
        destroy() {
            this.$inertia.delete(this.route('insurance.destroy-property', this.selectedProperty.id), {
                onFinish: () => {
                    this.showArchiveModal = false;
                }
            })
        },
        restoreProperty(property) {
            this.selectedProperty = property;
            this.showRestoreModal = true;
        },
        restore() {
            this.$inertia.put(this.route('insurance.restore-property', this.selectedProperty.id), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },

        getInsurance(property) {
            this.apiError = null;
            this.insuranceProperty = property
            this.insuranceForm.property_id = property.id
            this.insuranceForm.state = property.state
            this.insuranceForm.zip_code = property.zip_code
            this.insuranceForm.street_address = property.address_1
            this.insuranceForm.city = property.city
            this.insuranceForm.property_type = property.steadily_quote?.property_type ?? null
            this.insuranceForm.size_sqft = property.steadily_quote?.size_sqft ?? null
            this.insuranceForm.year_built = property.steadily_quote?.year_built ?? null
            this.insuranceForm.number_of_units = property.steadily_quote?.number_of_units ?? null
            this.insuranceForm.stories = property.steadily_quote?.stories ?? null

            if(property.steadily_quote) {
                this.quote = {
                    address: this.insuranceProperty.full_address,
                    size_sqft: this.insuranceForm.size_sqft,
                    year_built: this.insuranceForm.year_built,
                    monthly_premium: property.steadily_quote.formatted_dp3,
                    start_url: property.steadily_quote.start_url,
                };

            }

            this.quoteReceived = property.steadily_quote?.dp_3 ? true : false
            this.getInsuranceModal = true
        },

        async submitInsuranceForm() {
            this.apiError = null;
            this.isGetQuoteButtonDisabled = true;
            try {
                // Clear previous errors
                this.errors = {};

                // Send the form data to the backend via Axios
                const response = await axios.post(route('insurances.get-qoute'), this.insuranceForm);

                this.isGetQuoteButtonDisabled = false;

                if (response.data.status === false) {
                    this.apiError = response.data.message;
                    return;
                }

                this.quote = {
                    address: this.insuranceProperty.full_address,
                    size_sqft: this.insuranceForm.size_sqft,
                    year_built: this.insuranceForm.year_built,
                    monthly_premium: response.data.quote.formatted_dp3,
                    start_url: response.data.quote.start_url,
                };

                this.quoteReceived = true;

            } catch (error) {
                this.isGetQuoteButtonDisabled = false;
                if (error.response && error.response.data) {
                // If there are validation errors from the backend
                this.errors = error.response.data.errors;
                } else {
                console.error("An error occurred while fetching the quote:", error);
                }
            }

            router.reload();
        },
    },
}
</script>
